import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { makeAutoObservable } from 'mobx';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import {
    MultiDepositTabState,
    PaymentMethodType,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/multiDepositJourney/MultiDepositTab/MultiDepositTab.state';
import { RadioButtonUniversal } from 'src/domains/layouts/webview/components/RadioButton';
import {
    usePaymentMethodsConfig,
    renderPaymentIcon,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/multiDepositJourney/PaymentMethodsConfig/paymentMethodsConfig';
import { LogoYaspaOptions } from 'src/domains/common/paymentMethods/paymentOptions/LogoYaspaOptions';
import {
    DepositWrapper,
    Content,
    SectionCaption,
    PaymentMethodContainer,
    PaymentMethodOption,
    BankLogos,
    PaymentMethodInfo,
    PaymentMethodTitle,
    PaymentMethodDescription,
    RadioWrapper,
} from './PaymentMethodTab.style';

class PaymentMethodTabState {
    public constructor(
        private readonly multiDepositState: MultiDepositTabState,
        availablePaymentMethods: PaymentMethodType[]
    ) {
        makeAutoObservable(this);
        this.initializeDefaultSelection(availablePaymentMethods);
    }

    private initializeDefaultSelection(availablePaymentMethods: PaymentMethodType[]): void {
        const currentValue = this.multiDepositState.selectedPaymentMethod.value;

        const isCurrentValueAvailable = availablePaymentMethods.includes(currentValue);

        if (!isCurrentValueAvailable && availablePaymentMethods.length > 0) {
            const firstMethod = availablePaymentMethods[0];
            if (firstMethod !== undefined) {
                this.multiDepositState.selectedPaymentMethod.setValue(firstMethod);
                this.multiDepositState.setMethodToLocalStore(firstMethod);
            }
        } else if (isCurrentValueAvailable) {
            this.multiDepositState.selectedPaymentMethod.setValue(currentValue);
        }
    }
}

interface PaymentMethodTabPropsType {
    state: MultiDepositTabState;
    isSignup: boolean;
}

export const PaymentMethodTab = observer(
    'PaymentMethodTab',
    ({ state, isSignup }: PaymentMethodTabPropsType): JSX.Element => {
        const paymentMethods = usePaymentMethodsConfig();
        const availableMethodIds: PaymentMethodType[] = paymentMethods.map((method) => method.id);

        React.useState(() => new PaymentMethodTabState(state, availableMethodIds));

        const handleMethodChange = (method: PaymentMethodType): void => {
            state.selectedPaymentMethod.setValue(method);
            state.setMethodToLocalStore(method);
            state.selectedPaymentMethod.setAsVisited();
            state.errorMessagesState.clearErrorMessage();

            setTimeout(() => {
                state.stepsState.redirectToSetAmount();
            }, 200);
        };

        return (
            <DepositWrapper isSignup={isSignup}>
                <Content>
                    <SectionCaption isSignup={isSignup}>
                        <I18n
                            langKey='account.top-up.payment-method-caption'
                            defaultText='Select your preferred method to deposit funds into your account.'
                        />
                    </SectionCaption>

                    <PaymentMethodContainer>
                        {paymentMethods.map((method) => (
                            <PaymentMethodOption
                                key={method.id}
                                onClick={(): void => handleMethodChange(method.id)}
                                isSignup={isSignup}
                                data-test='payment-method-option'
                            >
                                {renderPaymentIcon(method, isSignup)}
                                <PaymentMethodInfo>
                                    <PaymentMethodTitle isSignup={isSignup}>{method.title}</PaymentMethodTitle>
                                    <PaymentMethodDescription isSignup={isSignup}>
                                        {method.description}
                                    </PaymentMethodDescription>
                                    {method.logos === true && (
                                        <BankLogos>
                                            <LogoYaspaOptions />
                                        </BankLogos>
                                    )}
                                </PaymentMethodInfo>
                                <RadioWrapper>
                                    <RadioButtonUniversal
                                        name={method.id}
                                        value={method.id}
                                        isActive={state.selectedPaymentMethod.value === method.id}
                                        isDisabled={false}
                                        size='small'
                                        version='light'
                                        onChange={(): void => handleMethodChange(method.id)}
                                    />
                                </RadioWrapper>
                            </PaymentMethodOption>
                        ))}
                    </PaymentMethodContainer>
                </Content>
            </DepositWrapper>
        );
    }
);
